/** ***********
*
* <manage-groups.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { Group } from 'src/app/models/user-right';
import { UserRightEffects } from 'src/app/store/effects/user-right.effects';
import { selectGroupsFilters$ } from 'src/app/store/selectors/filter.selectors';
import { selectGroups$, selectGroupsLoaded$ } from 'src/app/store/selectors/user-right.selectors';
import { DialogValidationComponent } from '../../shared/dialog/dialog-validation/dialog-validation.component';
import { DialogEditGroupComponent, GroupDialogData } from './dialog-edit-group/dialog-edit-group.component';
import * as featureActions from 'src/app/store/actions/user-right.action';
import { SnackbarComponent } from '../../shared/snackbar/snackbar';

@Component({
    selector: 'app-manage-groups',
    templateUrl: './manage-groups.component.html',
    styleUrls: ['./manage-groups.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ManageGroupsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  public groups$: Observable<Group[]>;
  public dataSource: MatTableDataSource<Group> = new MatTableDataSource();
  public displayedColumns: string[] = ['name', 'description', 'quantityOfUsers', 'quantityOfFamilies', 'actions'];
  public isLoadingTable$: Observable<boolean>;

  private _subscription = new Subscription();

  constructor(
    private store: Store,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _translate: TranslateService,
    private _userRightEffects: UserRightEffects,
    private _snackBar: SnackbarComponent,
  ) { }

  ngOnInit(): void {
    this.groups$ = this.store.select(selectGroups$);
    this._subscription.add(
      this.store.select(selectGroupsFilters$).subscribe(
        (groups) => {
          this._setTableData(groups);
          this._cd.markForCheck();
        }
      )
    );
    this.isLoadingTable$ = this.store.select(selectGroupsLoaded$).pipe(
      map((loaded) => !loaded),
      tap(() => { this._cd.markForCheck(); }),
    );
  }

  ngAfterViewInit(): void {
    this.refreshData();
  }

  /**
   * Refresh the data of the table
   */
  public refreshData(): void {
    this._setTableData([]);
    this.store.dispatch(new featureActions.FetchGroups());
  }

  /**
   * Set the data to the table
   * @param groups The groups
   */
  private _setTableData(groups: Group[]): void {
    this.dataSource.data = groups;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /**
   * Called when the user change the paginator page
   * - Aims to auto scroll to the top of the table
   */
  public scrollUp() {
    const table = document.querySelector('.mat-table');
    table.scrollIntoView({ behavior: 'smooth' });
    window.scrollTo(0, 0);
  }

  /**
   * Function called when the menu item to modify a group is clicked
   * @param group The group to modify
   */
  public onClickModifyGroup(group: Group): void {
    const data: GroupDialogData = {
      action: 'update',
      group,
    };
    this._dialog.open(DialogEditGroupComponent, {
      data,
      autoFocus: 'dialog',
    });
  }

  /**
   * Function called when the button to create a group is clicked
   */
  public onClickCreate(): void {
    const data: GroupDialogData = {
      action: 'create',
    };
    this._dialog.open(DialogEditGroupComponent, {
      data,
      autoFocus: 'first-tabbable'
    });
  }

  /**
   * Function called when the menu item to delete a group is clicked
   * @param group The group to delete
   */
  public onClickDeleteGroup(group: Group): void {
    const dialog = this._dialog.open(DialogValidationComponent, {
      data: {
        title: this._translate.instant('MANAGE_GROUPS_DELETE_WITH_NAME', { groupName: group.name }),
        textContent: this._translate.instant('MANAGE_GROUPS_DELETE_CONFIRM_TEXT', { groupName: group.name }),
        // cancelText
        validateText: this._translate.instant('DIALOG_VALIDATE_OPTION'),
        dangerousAction: true,
      },
    });
    this._subscription.add(
      dialog.componentInstance.onValidateEvent.subscribe(
        () => this._deleteGroup(group, dialog)
      )
    );
  }

  /**
   * Delete the group
   * @param group The group to delete
   * @param dialogRef The confirmation dialog
   */
  private _deleteGroup(group: Group, dialogRef?: MatDialogRef<any>): void {
    this.store.dispatch(new featureActions.DeleteGroup({ group }));
    this._subscription.add(
      this._userRightEffects.effectSubject.pipe(
        filter(result => result.action === featureActions.ActionTypes.DELETE_GROUP),
        take(1),
        filter(({ result }) => result === 'success'),
      ).subscribe(
        () => {
            dialogRef?.close();
            const text = this._translate.instant('BANNER_SUCCESS_DELETE', { item: group.name });
            this._snackBar.open(text, 'green-snackbar', 5000);
        }
      )
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

}
